// ============================================
// SystemParametersTab
// Tham số vận hành khoá–giá trị (sheet 2 — mục #234)
// ============================================
//
// GET /settings/ trả danh sách `SystemSetting { key, value, description,
// is_public }`. Khác hẳn bốn tab còn lại của màn Cấu hình: chúng là form có
// sẵn field, còn đây là bảng khoá do BE định nghĩa và FE không biết trước.
//
// LƯU TỪNG KHOÁ MỘT qua PATCH /settings/{key}/, không dùng bulk-update. Lý do
// không phải kỹ thuật mà là nhật ký: mỗi lần sửa phải ra đúng một dòng audit
// với `details.old_value`/`new_value`, để lịch sử bên dưới dựng được diff. Gửi
// cả lô thì một dòng log gộp nhiều khoá, không truy được ai đổi khoá nào.
//
// NHẬT KÝ do /audit/ giữ, KHÔNG phải /settings/ (BE xác nhận 09/09/2026):
//   GET /audit/?resource_type=SystemSetting&resource_id=<key>
// `resource_id` chính là tên khoá, không phải UUID — đây là ngoại lệ so với
// mọi chỗ khác dùng audit, nên đừng "sửa" nó thành id.

import { useMemo, useState } from 'react';
import type { SystemSetting } from '@conai/shared-types';
import { useI18n } from '../../../i18n';
import { useSystemSettings, useUpdateSystemSetting, useAuditLogsForEntity } from '../../../hooks';

/** Khoá nào cũng chỉ có hai nhóm; tách ra vì rủi ro khi sửa rất khác nhau. */
type Scope = 'all' | 'public' | 'internal';

/**
 * `value` có thể là chuỗi, số, boolean hoặc object JSON. Ô nhập là một dòng
 * text, nên object phải serialize để sửa được — và parse lại lúc lưu.
 */
function valueToText(value: SystemSetting['value']): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Chuỗi người dùng gõ -> giá trị gửi lên.
 *
 * Giữ NGUYÊN KIỂU của giá trị cũ thay vì đoán từ chuỗi mới: khoá đang là số mà
 * gõ "0123" thì phải ra số 123, còn khoá đang là chuỗi mã bưu chính "0123" thì
 * phải giữ nguyên chuỗi. Đoán mù theo nội dung là sai một trong hai trường hợp.
 */
function textToValue(text: string, previous: SystemSetting['value']): SystemSetting['value'] {
  const trimmed = text.trim();

  if (typeof previous === 'boolean') return trimmed.toLowerCase() === 'true';
  if (typeof previous === 'number') {
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : previous;
  }
  if (typeof previous === 'object' && previous !== null) {
    try {
      return JSON.parse(trimmed);
    } catch {
      // JSON hỏng: trả về giá trị CŨ để không ghi đè object bằng một chuỗi
      // rác. Phía gọi kiểm tra riêng và chặn trước khi tới đây.
      return previous;
    }
  }
  return text;
}

function isJsonBroken(text: string, previous: SystemSetting['value']): boolean {
  if (typeof previous !== 'object' || previous === null) return false;
  try {
    JSON.parse(text.trim());
    return false;
  } catch {
    return true;
  }
}

/** Lịch sử thay đổi của MỘT khoá, đọc từ audit log. */
function SettingHistory({ settingKey }: { settingKey: string }) {
  const { t } = useI18n();
  const { data, isLoading } = useAuditLogsForEntity('SystemSetting', settingKey, { limit: 20 });

  // `useAuditLogsForEntity` LUÔN trả `PaginatedResponse<AuditLog>`. Bản trước
  // còn thêm nhánh `Array.isArray(data)` phòng hờ — nhánh chết, và tai hại ở
  // chỗ nó biến `rows` thành union hai kiểu mảng khiến `log` bị suy ra `any`,
  // mất sạch kiểm tra kiểu cho `log.details` ngay bên dưới.
  const rows = data?.results ?? [];

  if (isLoading) {
    return <p className="px-4 py-3 text-xs text-slate-500">{t('loading')}</p>;
  }
  if (rows.length === 0) {
    // Không có log KHÔNG có nghĩa là khoá chưa từng đổi — audit chỉ ghi từ khi
    // tính năng được bật. Nói đúng mức đó, đừng khẳng định "chưa ai sửa".
    return <p className="px-4 py-3 text-xs text-slate-500">{t('settingsNoHistory')}</p>;
  }

  return (
    <div className="divide-y divide-slate-100">
      {rows.map((log) => {
        const details = (log.details ?? {}) as Record<string, unknown>;
        const oldValue = details.old_value;
        const newValue = details.new_value;
        return (
          <div key={log.id} className="px-4 py-2 text-xs">
            <div className="flex flex-wrap items-center gap-x-2 text-slate-500">
              <span className="font-semibold text-slate-700">{log.action}</span>
              <span>{log.actor_email || log.user_email || '—'}</span>
              <span>{log.created_at ? new Date(log.created_at).toLocaleString() : ''}</span>
            </div>
            {oldValue !== undefined || newValue !== undefined ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <code className="rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through">
                  {valueToText(oldValue as SystemSetting['value'])}
                </code>
                <span className="text-slate-400">→</span>
                <code className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">
                  {valueToText(newValue as SystemSetting['value'])}
                </code>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function SystemParametersTab() {
  const { t } = useI18n();
  const [scope, setScope] = useState<Scope>('all');
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useSystemSettings();
  const { mutate: save, isPending, variables } = useUpdateSystemSetting();
  /**
   * Khoá đang được lưu. `isPending` là state của MỘT mutation dùng chung, nên
   * nếu chỉ xét nó thì lưu một khoá là nút Lưu của cả bảng cùng xám — người
   * dùng tưởng màn hình treo. `variables.key` cho biết đúng dòng nào đang bay.
   */
  const savingKey = isPending ? variables?.key : null;

  /** Bản nháp cho những khoá đang sửa dở. Khoá không có trong đây = chưa đụng. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  /** Khoá đang mở lịch sử. Chỉ một, để không bắn hàng chục request audit cùng lúc. */
  const [openHistory, setOpenHistory] = useState<string | null>(null);

  const settings = useMemo(() => data ?? [], [data]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return settings.filter((s) => {
      if (scope === 'public' && !s.is_public) return false;
      if (scope === 'internal' && s.is_public) return false;
      if (!q) return true;
      return (
        s.key.toLowerCase().includes(q) ||
        (s.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [settings, scope, search]);

  const scopeTabs: { id: Scope; label: string }[] = [
    { id: 'all', label: t('all') },
    { id: 'public', label: t('settingsScopePublic') },
    { id: 'internal', label: t('settingsScopeInternal') },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex overflow-hidden rounded-lg border border-slate-200">
          {scopeTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setScope(tab.id)}
              className={`px-3 py-2 text-sm font-semibold transition-colors ${
                scope === tab.id ? 'bg-primary-50 text-primary-700' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('settingsSearchKey')}
          className="h-10 min-w-[200px] flex-1 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-primary-400"
        />
      </div>

      {error ? (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {error instanceof Error ? error.message : t('settingsLoadFailed')}
        </div>
      ) : isLoading ? (
        <p className="py-8 text-center text-sm text-slate-500">{t('loading')}</p>
      ) : visible.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500">{t('noResults')}</p>
      ) : (
        <div className="space-y-2">
          {visible.map((setting) => {
            const original = valueToText(setting.value);
            const draft = drafts[setting.key];
            const text = draft ?? original;
            const dirty = draft !== undefined && draft !== original;
            const broken = dirty && isJsonBroken(text, setting.value);

            return (
              <div key={setting.key} className="rounded-xl border border-slate-200">
                <div className="flex flex-wrap items-start gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="text-sm font-semibold text-slate-800">{setting.key}</code>
                      {/* Nhãn công khai là CẢNH BÁO, không phải trang trí: sửa
                          khoá này là đổi hành vi app khách ngay lập tức. */}
                      {setting.is_public ? (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                          {t('settingsScopePublic')}
                        </span>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                          {t('settingsScopeInternal')}
                        </span>
                      )}
                    </div>
                    {setting.description ? (
                      <p className="mt-1 text-xs text-slate-500">{setting.description}</p>
                    ) : null}
                  </div>

                  <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:flex-1">
                    <input
                      value={text}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [setting.key]: e.target.value }))
                      }
                      className={`h-10 min-w-0 flex-1 rounded-lg border px-3 font-mono text-sm outline-none focus:ring-2 focus:ring-primary-200 ${
                        broken ? 'border-red-300 bg-red-50' : 'border-slate-300 focus:border-primary-400'
                      }`}
                    />
                    <button
                      type="button"
                      // Chỉ bật khi THẬT SỰ có thay đổi hợp lệ. Cho bấm lúc
                      // không đổi gì là đẻ ra một dòng audit rỗng, làm nhiễu
                      // đúng cái nhật ký mà màn này dựng lên để phục vụ.
                      disabled={!dirty || broken || savingKey === setting.key}
                      onClick={() =>
                        save(
                          { key: setting.key, value: textToValue(text, setting.value) },
                          {
                            onSuccess: () =>
                              setDrafts((prev) => {
                                const next = { ...prev };
                                delete next[setting.key];
                                return next;
                              }),
                          }
                        )
                      }
                      className="shrink-0 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-40"
                    >
                      {savingKey === setting.key ? t('saving') : t('saveBtn')}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setOpenHistory((prev) => (prev === setting.key ? null : setting.key))
                      }
                      className="shrink-0 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
                    >
                      {t('settingsHistory')}
                    </button>
                  </div>
                </div>

                {broken ? (
                  <p className="px-4 pb-3 text-xs font-semibold text-red-600">
                    {t('settingsInvalidJson')}
                  </p>
                ) : null}

                {openHistory === setting.key ? (
                  <div className="border-t border-slate-100 bg-slate-50/60">
                    <SettingHistory settingKey={setting.key} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
